import { ClientEvents } from 'common/events/ClientEvents';
import React, { useEffect } from 'react';
import {
  LEAD_CAPTURE_CONTACT_STORAGE_KEY,
  LeadCaptureContact,
} from 'src/pages/consumer/lead-capture-modal/leadCaptureStorage';
import { useTracker } from 'src/shared/hooks/useTracker';
import { useLocalStorage } from 'src/shared/storage/Storage';

interface LeadCaptureAutofillParams {
  email: string;
  onEmailChange: (event: React.FormEvent<HTMLInputElement>) => void;
  phone?: string;
  onPhoneChange?: (event: React.FormEvent<HTMLInputElement>) => void;
}

const syntheticInputEvent = (value: string): React.FormEvent<HTMLInputElement> =>
  ({
    currentTarget: { value, selectionStart: value.length, setSelectionRange: () => undefined },
    preventDefault: () => undefined,
    stopPropagation: () => undefined,
  } as unknown as React.FormEvent<HTMLInputElement>);

/**
 * Prefills the auth email and phone fields once on mount from a stored lead-capture
 * contact. The two fields autofill independently — each fills only when empty — and the
 * {@link ClientEvents.LEAD_CAPTURE_AUTOFILL_APPLIED} event fires solely from the email
 * branch.
 *
 * Email is the contact's required field and the only one shared across auth screens, so
 * it fills (and the event fires) exactly once — on whichever screen mounts first with an
 * empty field. Phone lives only on the sign-up screen and fills without its own event, so
 * a returning lead who passes through both screens still sees a single event.
 */
export const useLeadCaptureAutofill = ({
  email,
  onEmailChange,
  phone,
  onPhoneChange,
}: LeadCaptureAutofillParams): void => {
  const [leadContact] = useLocalStorage<LeadCaptureContact>(LEAD_CAPTURE_CONTACT_STORAGE_KEY, null);
  const tracker = useTracker();

  useEffect(() => {
    if (!leadContact) return;

    if (leadContact.phone && !phone) {
      onPhoneChange?.(syntheticInputEvent(leadContact.phone));
    }

    if (leadContact.email && !email) {
      onEmailChange(syntheticInputEvent(leadContact.email));
      tracker.track(ClientEvents.LEAD_CAPTURE_AUTOFILL_APPLIED, {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
